import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import { Localize } from "../../../app/lang/langs";
import styles from "../Dashboard.module.css";
import moment from "moment";
import NewsModal from "./NewsModal";
import addIcon from "../../../assets/images/add.png";
import addIconFocus from "../../../assets/images/addFocus.png";
import ImageButton from "../../buttons/ImageButton";
import { checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import AddNewsModal from "./AddNewsModal";
import deleteIcon from "../../../assets/images/delete.png";
import deleteIconFocus from "../../../assets/images/delete-orange.png";
import { confirmDialog } from "primereact/confirmdialog";
import { deleteNews, setNewsList } from "../../../containers/dashboard/dashboardSlice";

export default function NewsTable({
    showHeader = true,
    showAddButton = false,
    tableHeight = 260,
}) {

    const dispatch = useDispatch();
    const isMobile = useSelector((state) => state.settings.isMobile);
    const mobile = isMobile === 2;
    const newsList = useSelector((state) => state.dashboard.news)
    const [selectedNews, setSelectedNews] = useState(null)
    const [visible, setVisible] = useState(false)

    const onNewsSelect = (row) => {
        setSelectedNews(row)
    }

    const rowClassName = (row) => {
        const isSeen = row.seen;
        return isSeen ? "" : "news-row-unread";
    }

    const deleteNewsBody = (row) => {
        if (checkPermission(config.scopes.news, config.permissionTypes.edit))
            return (<ImageButton
                tooltip={Localize("DELETE_NEWS")}
                image={deleteIcon}
                imageFocus={deleteIconFocus}
                onClick={() => { confirmDeleteNotification(row.id) }}
                active={true}
                width={32}
            />)
    }

    const confirmDeleteNotification = (newsId) => {

        const accept = async (newsId) => {
            const response = await dispatch(deleteNews({ newsId: newsId })).unwrap();
            if (response && response.responseData && response.responseData.status) {
                dispatch(setNewsList(newsList.filter(n => n.id !== newsId)))
            }
        }

        const reject = () => { }
        confirmDialog({
            message: Localize("ARE_YOU_SURE_YOU_WANT_TO_DELETE_THIS_NEWS"),
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            accept: () => accept(newsId),
            reject
        })
    }

    const rows = useMemo(() => {
        if (!newsList || !newsList.length) return [];
        return newsList.map((news, idx) => ({
            id: news.id ?? idx,
            title: news.title || "",
            message: news.message || "",
            created_by: news.created_by || "",
            created_at: news.created_at,
            raw: news,
        }));
    }, [newsList]);

    const columns = useMemo(
        () => mobile ? [
            {
                field: "title",
                headerName: Localize("Title"),
                flex: 1,
                minWidth: 120,
                renderCell: (params) => (
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        lineHeight: 1.3,
                        padding: "6px 0",
                        overflow: "hidden",
                    }}>
                        <span style={{
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}>
                            {params.value}
                        </span>
                        <span style={{
                            fontSize: "0.75rem",
                            color: "#888",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}>
                            {params.row.message}
                        </span>
                    </div>
                ),
            },
            {
                field: "created_at",
                headerName: Localize("created_at"),
                width: 100,
                valueFormatter: (value) =>
                    value ? moment(value).format("DD/MM/YY") : "",
            },
            {
                field: "actions",
                headerName: "",
                sortable: false,
                filterable: false,
                width: 48,
                renderCell: (params) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        {deleteNewsBody(params.row.raw)}
                    </div>
                ),
            },
        ] : [
            {
                field: "title",
                headerName: Localize("Title"),
                flex: 1,
                minWidth: 140,
                renderCell: (params) => (
                    <div
                        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={params.value}
                    >
                        {params.value}
                    </div>
                ),
            },
            {
                field: "message",
                headerName: Localize("Message"),
                flex: 2,
                minWidth: 220,
                renderCell: (params) => (
                    <div
                        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                        title={params.value}
                    >
                        {params.value}
                    </div>
                ),
            },
            {
                field: "created_by",
                headerName: Localize("created_by"),
                flex: 1,
                minWidth: 140,
            },
            {
                field: "created_at",
                headerName: Localize("created_at"),
                flex: 1,
                minWidth: 160,
                valueFormatter: (value) =>
                    value ? moment(value).format("DD/MM/YYYY HH:mm") : "",
            },
            {
                field: "actions",
                headerName: "",
                sortable: false,
                filterable: false,
                width: 64,
                renderCell: (params) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        {deleteNewsBody(params.row.raw)}
                    </div>
                ),
            },
        ],
        [deleteNewsBody, mobile]
    );

    return (
        <div
            className={styles.vcompCard}
            style={{
                background: "rgba(255, 255, 255, 0.72)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                borderRadius: mobile ? "8px" : "16px",
                border: mobile ? "none" : "1px solid rgba(255, 144, 21, 0.08)",
                boxShadow: mobile ? "none" : "0 4px 24px rgba(255, 144, 21, 0.06), 0 1px 6px rgba(0, 0, 0, 0.04)",
                padding: mobile ? "4px" : "16px",
                transition: "box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1), border-color 250ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
        >
            {(showHeader || showAddButton) && (
                <Box
                    className="d-flex justify-content-between align-items-center"
                    sx={{ mb: "12px" }}
                >
                    {showHeader ? (
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                letterSpacing: "-0.02em",
                                fontSize: "0.95rem",
                                color: "#2d2a26",
                            }}
                        >
                            {Localize("NEWS")}
                        </Typography>
                    ) : (
                        <Box />
                    )}
                    {showAddButton && checkPermission(config.scopes.news, config.permissionTypes.edit) && (
                        <ImageButton
                            tooltip={Localize("ADD_NEWS")}
                            image={addIcon}
                            imageFocus={addIconFocus}
                            onClick={() => { setVisible(true) }}
                            active={true}
                            width={32}
                        />
                    )}
                </Box>
            )}
            <Box sx={{ height: tableHeight, width: "100%" }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    disableRowSelectionOnClick
                    density={"standard"}
                    onRowClick={(params) => onNewsSelect(params.row.raw)}
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10, page: 0 } },
                    }}
                    getRowClassName={(params) => rowClassName(params.row.raw)}
                    sx={{
                        border: mobile ? "none" : "1px solid rgba(255, 144, 21, 0.08)",
                        borderRadius: mobile ? "8px" : "14px",
                        backgroundColor: "rgba(255, 255, 255, 0.60)",
                        backdropFilter: "blur(10px)",
                        overflow: "hidden",
                        fontSize: mobile ? "0.8rem" : "0.875rem",
                        fontWeight: 400,
                        letterSpacing: "0.01em",

                        /* Unread row pulse animation */
                        "@keyframes newsRowPulse": {
                            "0%": {
                                backgroundColor: "rgba(255, 193, 7, 0.16)",
                                boxShadow: "inset 4px 0 0 rgba(255, 193, 7, 0.9)",
                            },
                            "50%": {
                                backgroundColor: "rgba(255, 193, 7, 0.05)",
                                boxShadow: "inset 4px 0 0 rgba(255, 193, 7, 0.5)",
                            },
                            "100%": {
                                backgroundColor: "rgba(255, 193, 7, 0.10)",
                                boxShadow: "inset 4px 0 0 rgba(255, 193, 7, 0.7)",
                            },
                        },
                        "& .news-row-unread": {
                            backgroundColor: mobile ? "rgba(255, 144, 21, 0.10)" : "rgba(255, 193, 7, 0.10)",
                            boxShadow: mobile ? "inset 4px 0 0 #FF9015" : "inset 4px 0 0 rgba(255, 193, 7, 0.7)",
                            animation: "newsRowPulse 2.2s ease-in-out 0s 2",
                            fontWeight: mobile ? 600 : undefined,
                        },
                        "& .news-row-unread:hover": {
                            backgroundColor: mobile ? "rgba(255, 144, 21, 0.16) !important" : "rgba(255, 193, 7, 0.14) !important",
                        },

                        /* Column headers */
                        "& .MuiDataGrid-columnHeaders": {
                            background: "linear-gradient(135deg, rgba(255, 144, 21, 0.05) 0%, rgba(255, 193, 0, 0.03) 100%)",
                            borderBottom: "1px solid rgba(255, 144, 21, 0.10)",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            color: "#6b6560",
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 600,
                        },
                        "& .MuiDataGrid-columnSeparator": {
                            visibility: "hidden",
                        },

                        /* Cells */
                        "& .MuiDataGrid-cell": {
                            borderBottom: "1px solid rgba(0, 0, 0, 0.04)",
                            color: "#3a3632",
                            transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)",
                        },
                        "& .MuiDataGrid-cell:focus": {
                            outline: "none",
                        },
                        "& .MuiDataGrid-cell:focus-within": {
                            outline: "none",
                        },

                        /* Rows */
                        "& .MuiDataGrid-row": {
                            cursor: "pointer",
                            transition: "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 180ms cubic-bezier(0.4, 0, 0.2, 1)",
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "rgba(255, 144, 21, 0.05) !important",
                        },

                        /* Footer / pagination */
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "1px solid rgba(255, 144, 21, 0.08)",
                            background: "rgba(255, 255, 255, 0.40)",
                        },
                        "& .MuiTablePagination-root": {
                            color: "#6b6560",
                            fontSize: "0.8rem",
                        },

                        /* Scrollbar */
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                            width: "6px",
                            height: "6px",
                        },
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                            borderRadius: "3px",
                            background: "rgba(255, 144, 21, 0.18)",
                        },
                        "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                            background: "transparent",
                        },
                    }}
                />
            </Box>
            <NewsModal selectedNews={selectedNews} onHide={() => setSelectedNews(null)} />
            <AddNewsModal visible={visible} setVisible={setVisible} />
        </div>
    );
}
